import { AsyncAutocomplete } from '@foam/buoy/src/components/Autocomplete/AsyncAutocomplete';
import {
  InputSkeleton,
  PresentationInputButton,
} from '@foam/buoy/src/components/Input/Input';
import {
  UiOptionHelper,
  UiOptionTypes,
} from '@foam/buoy/src/components/combobox/types';
import { cn } from '@foam/buoy/src/lib/utils';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Avatar } from 'ui-components';
import { Creator } from '~/clientApi/creator.model';
import { getMainPlatformDataV2 } from '~/core/utils/creator/creator';
import { CONTENT_SEARCH_PARAMS } from '~/hooks/useContentSearch';
import { ALL_TALENT_ROSTER_QUERY_KEY } from '~/queries/keys';
import { useContentSearchFilterContext } from '~/screens/SearchContent/ContentSearchFilterContext';
import { useSearchContentParams } from '~/screens/SearchContent/useSearchContentParams';
import { selectTalentManager } from '~/selectors/talent-manager';

import {
  getDefinitelyUniqueIdFromCreator,
  useCreatorAutocompleteQuery,
} from './useCreatorAutocompleteQuery';
import { useDefaultSelectedCreator } from './useDefaultSelectedCreator';

function CreatorSelectorInternal({
  defaultSelectedValue,
}: {
  defaultSelectedValue: UiOptionHelper<{ creator: Creator }> | null;
}) {
  const { onChange, setSelectedCreator } = useContentSearchFilterContext();
  const manager = useSelector(selectTalentManager);
  const managerId = manager?.id;

  const [selectedValue, setSelectedValue] = useState<UiOptionHelper<{
    creator: Creator;
  }> | null>(defaultSelectedValue);

  const updateSelectedValue = useCallback(
    (
      maybeValue: UiOptionHelper<{
        creator: Creator;
      }> | null,
    ) => {
      setSelectedCreator(maybeValue?.creator);
      setSelectedValue(maybeValue);
      onChange({
        [CONTENT_SEARCH_PARAMS.creator]: maybeValue
          ? maybeValue.creator.id
          : undefined,
      });
    },
    [onChange, setSelectedCreator],
  );

  return (
    <>
      <AsyncAutocomplete
        value={selectedValue}
        placeholder="Search by name or handle..."
        beginTypingMessage="Search by name or handle..."
        noItemsMessage="No talent found. Check name or handle."
        query={useCreatorAutocompleteQuery}
        queryOptions={{
          enabled: true,
          queryKey: [
            ALL_TALENT_ROSTER_QUERY_KEY,
            {
              managerId,
              filters: [],
              sortField: 'relevance',
              sortDirection: 'asc',
              searchTerm: '',
            },
          ],
        }}
        onChange={updateSelectedValue}
        slots={{
          SelectedValue: CreatorSelectorSelectedValue,
          SingleItem: CreatorSelectorItem,
        }}
      />
    </>
  );
}

function CreatorSelectorSelectedValue({
  value: { creator },
  clearButton,
  className,
  ...rest
}: React.ComponentProps<typeof PresentationInputButton> & {
  value: UiOptionHelper<{ creator: Creator }>;
  clearButton: React.ReactNode;
}) {
  const { name, avatar } = getMainPlatformDataV2(creator);

  return (
    <div className={cn('relative', className)}>
      <PresentationInputButton
        className={cn('flex items-center px-1 gap-2')}
        {...rest}
      >
        <Avatar
          size="sm"
          alt={name}
          sx={{ alignSelf: 'center' }}
          src={avatar}
        />
        <div className="flex flex-col">
          <div className="font-500">{creator.fullName}</div>
        </div>
      </PresentationInputButton>
      {clearButton}
    </div>
  );
}

function CreatorSelectorItem({
  item,
}: {
  item: UiOptionHelper<{ creator: Creator }>;
}) {
  const { name, identifierForUi, avatar } = getMainPlatformDataV2(item.creator);

  return (
    <div className="flex items-center gap-2">
      <Avatar size="md" alt={name} sx={{ alignSelf: 'center' }} src={avatar} />
      <div className="flex flex-col gap-0p5">
        <div className="text-14 font-500 text-neutral-500">{name}</div>
        <div className="text-12 text-neutral-400">{identifierForUi}</div>
      </div>
    </div>
  );
}

export function CreatorSelector() {
  /**
   * This component is just a wrapper around the real CreatorSelector to  ensure
   * that the CreatorSelector is rendered with preselected value when the page is loaded
   * with creator id in the url.
   */
  const { onChange } = useContentSearchFilterContext();
  const filters = useSearchContentParams();
  const [creatorIdFromUrl, setCreatorIdFromUrl] = useState<string | undefined>(
    () => filters?.creator,
  );

  const handleError = useCallback(() => {
    // behave as if the creator id is not present in the url if fetching creator data fails
    setCreatorIdFromUrl(undefined);
    onChange({
      [CONTENT_SEARCH_PARAMS.creator]: undefined,
    });
  }, [onChange]);

  const { data } = useDefaultSelectedCreator(creatorIdFromUrl, handleError);

  const selectedCreator = data as Creator | undefined;

  const defaultSelectedValue = useMemo(() => {
    return selectedCreator
      ? {
          creator: selectedCreator,
          optionType: UiOptionTypes.item,
          uniqueKey: getDefinitelyUniqueIdFromCreator(selectedCreator),
          label: selectedCreator.fullName,
          value: selectedCreator.id,
        }
      : null;
  }, [selectedCreator]);

  if (creatorIdFromUrl && !defaultSelectedValue) {
    return <InputSkeleton />;
  }

  return (
    <CreatorSelectorInternal defaultSelectedValue={defaultSelectedValue} />
  );
}
